import { FunctionComponent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { TABS } from '../constants/checkoutConstants';

import CheckoutCreditCardSubmit from './creditCard/CheckoutCreditCardSubmit';
import CheckoutCheckSubmit from './check/CheckoutCheckSubmit';
import CheckoutWireTransferSubmit from './wireTransfer/CheckoutWireTransferSubmit';
import CheckoutPayPalSubmit from './payPal/CheckoutPayPalSubmit';
import CheckoutBankAccountsSubmit from './bankAccount/CheckoutBankAccountsSubmit';
import CheckoutBankAccountsSubmitStripe from './stripe/CheckoutBankAccountsSubmitStripe';
import CheckoutApplePaySubmit from './applePay/CheckoutApplePaySubmit';

import { CheckoutPaymentStepSubmit_viewer$key } from './__generated__/CheckoutPaymentStepSubmit_viewer.graphql';
import { CheckoutPaymentStepSubmit_buyer$key } from './__generated__/CheckoutPaymentStepSubmit_buyer.graphql';
import { CheckoutPaymentStepSubmit_transaction$key } from './__generated__/CheckoutPaymentStepSubmit_transaction.graphql';

type CheckoutPaymentStepSubmitProps = {
    isComputing: boolean;
    paymentMethod: (typeof TABS)[keyof typeof TABS];
    isPaymentLoading: boolean;
    onValidationError: ({ isValid, errors }: $TSFixMe) => void;
    viewer: CheckoutPaymentStepSubmit_viewer$key;
    buyer: CheckoutPaymentStepSubmit_buyer$key;
    transaction: CheckoutPaymentStepSubmit_transaction$key | null;
};

export const CheckoutPaymentStepSubmit: FunctionComponent<
    CheckoutPaymentStepSubmitProps
> = props => {
    const { isComputing, paymentMethod, onValidationError, isPaymentLoading } = props;

    const viewer = useFragment(
        graphql`
            fragment CheckoutPaymentStepSubmit_viewer on Viewer {
                ...CheckoutCreditCardSubmit_viewer
                ...CheckoutBankAccountsSubmit_viewer
                ...CheckoutBankAccountsSubmitStripe_viewer
                stripeIsEnabled: featureFlag(feature: "stripeCheckout")
            }
        `,
        props.viewer
    );

    const buyer = useFragment(
        graphql`
            fragment CheckoutPaymentStepSubmit_buyer on User {
                ...CheckoutCreditCardSubmit_buyer
                ...CheckoutWireTransferSubmit_buyer
                ...CheckoutPayPalSubmit_buyer
                ...CheckoutBankAccountsSubmit_buyer
                ...CheckoutBankAccountsSubmitStripe_buyer
            }
        `,
        props.buyer
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutPaymentStepSubmit_transaction on Transaction {
                ...CheckoutBankAccountsSubmit_transaction
                ...CheckoutBankAccountsSubmitStripe_transaction
                eligibleStripeACH: eligiblePaymentMethods(
                    paymentMethods: [ACH]
                    paymentProcessor: STRIPE
                )
            }
        `,
        props.transaction
    );

    const sharedProps = {
        isComputing,
        onValidationError,
        isPaymentLoading,
        viewer,
        buyer,
        transaction,
    };

    const isStripeACHEnabled = viewer?.stripeIsEnabled && !!transaction?.eligibleStripeACH?.length;

    switch (paymentMethod) {
        case TABS.TAB_CREDIT_CARD:
            return <CheckoutCreditCardSubmit {...sharedProps} />;
        case TABS.TAB_CHECK:
            return <CheckoutCheckSubmit />;
        case TABS.TAB_WIRE_TRANSFER:
            return <CheckoutWireTransferSubmit {...sharedProps} />;
        case TABS.TAB_PAYPAL_ACCOUNT:
            return <CheckoutPayPalSubmit {...sharedProps} />;
        case TABS.TAB_BANK_ACCOUNT:
            if (isStripeACHEnabled) {
                return <CheckoutBankAccountsSubmitStripe {...sharedProps} />;
            } else {
                return <CheckoutBankAccountsSubmit {...sharedProps} />;
            }
        case TABS.TAB_APPLE_PAY:
            return <CheckoutApplePaySubmit />;
        default:
            return null;
    }
};
